// Real-time WhatsApp Business Calling — the first live-push consumer in the
// back-office. Mounted ONCE at the App root (not per-page) so the WebSocket
// connection survives navigation: a buyer's call can't be allowed to silently
// vanish just because the operator was looking at an order when it rang.
import { useState, useEffect, useRef, useCallback } from 'react';
import { getWhatsAppWsUrl, acceptWhatsAppCall, rejectWhatsAppCall, endWhatsAppCall, loadWhatsAppCalls } from './backOfficeApi';
import { toast } from './buses';
import { createReconnectBackoff } from './reconnectBackoff';

export function useCallCenter() {
  const [connection, setConnection] = useState('connecting'); // connecting | connected | reconnecting | offline | gave-up
  const [ringingCalls, setRingingCalls] = useState([]);
  const [activeCall, setActiveCall] = useState(null);
  const [callStartedAt, setCallStartedAt] = useState(null);
  const [muted, setMuted] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyStatus, setHistoryStatus] = useState('idle'); // idle | loading | ready | error

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const audioElRef = useRef(null);
  const activeCallRef = useRef(null);
  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);
  // Lets `reconnect()` (below) reach the CURRENT effect's connect closure —
  // there's no live-call polling fallback like Chat has, so once the backoff
  // gives up an operator needs a real way back in short of reloading the page.
  const manualReconnectRef = useRef(() => {});

  const refreshHistory = useCallback(async () => {
    setHistoryStatus('loading');
    try {
      const rows = await loadWhatsAppCalls({ limit: 30 });
      setHistory(rows);
      setHistoryStatus('ready');
    } catch {
      setHistoryStatus('error');
    }
  }, []);

  useEffect(() => { refreshHistory(); }, [refreshHistory]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer;
    const backoff = createReconnectBackoff();

    // Schedules the next attempt, or gives up for good once the backoff is
    // exhausted (a genuinely down backend, not a transient blip). There's no
    // passive fallback for a missed live call, so give-up is a distinct,
    // visible state — the operator gets a manual "Reconnect" control instead
    // of the app silently never trying again.
    const retryOrGiveUp = () => {
      const delay = backoff.next();
      if (delay == null) { setConnection('gave-up'); return; }
      retryTimer = setTimeout(connect, delay);
    };

    const connect = () => {
      setConnection((c) => (c === 'connected' ? 'reconnecting' : 'connecting'));
      let ws;
      try {
        ws = new WebSocket(getWhatsAppWsUrl());
      } catch {
        setConnection('offline');
        retryOrGiveUp();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => { if (!cancelled) { setConnection('connected'); backoff.reset(); } };
      ws.onclose = (e) => {
        if (cancelled) return;
        setConnection('offline');
        if (e.code !== 1000) retryOrGiveUp();
      };
      ws.onerror = () => { if (!cancelled) setConnection('offline'); };
      ws.onmessage = (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        if (msg.type === 'incoming_call') {
          setRingingCalls((prev) => (prev.some((c) => c.callId === msg.data.callId) ? prev : [...prev, { ...msg.data, ringingSince: Date.now() }]));
          toast('Incoming WhatsApp call', 'info', msg.data.contactName || msg.data.from);
        } else if (msg.type === 'call_claimed') {
          // Another agent answered first — every OTHER dashboard's ringing
          // entry for this call is stale now. No toast: this fires on every
          // agent's socket including the one who just answered it themselves
          // (their own UI already reflects "on call"), so an intrusive alert
          // here would be noise for the winner and unnecessary drama for
          // bystanders — the ring silently disappearing is signal enough.
          const { callId } = msg.data || {};
          if (!callId) return;
          setRingingCalls((prev) => (prev.some((c) => c.callId === callId) ? prev.filter((c) => c.callId !== callId) : prev));
        } else if (msg.type === 'call_ended') {
          // The far end hung up — either before anyone answered (clear the
          // ghost ring) or mid-conversation (stop the dead-air audio and free
          // the mic/peer connection; nobody clicked our own Hang up button).
          const { callId } = msg.data || {};
          if (!callId) return;
          setRingingCalls((prev) => (prev.some((c) => c.callId === callId) ? prev.filter((c) => c.callId !== callId) : prev));
          if (activeCallRef.current?.callId === callId) {
            streamRef.current?.getTracks().forEach((t) => t.stop());
            pcRef.current?.close();
            pcRef.current = null;
            streamRef.current = null;
            setActiveCall(null);
            setCallStartedAt(null);
            toast('Call ended', 'info', 'The other party hung up');
            refreshHistory();
          }
        }
      };
    };
    // Manual "Reconnect" (only meaningful after a give-up): drop any pending
    // retry, start the attempt count over, and try again right away.
    manualReconnectRef.current = () => { clearTimeout(retryTimer); backoff.reset(); connect(); };
    connect();
    return () => { cancelled = true; clearTimeout(retryTimer); wsRef.current?.close(1000); };
  }, []);

  const reconnect = useCallback(() => { manualReconnectRef.current(); }, []);
  const attachAudioEl = useCallback((el) => { audioElRef.current = el; }, []);

  const answerCall = useCallback(async (call) => {
    if (!call.sdpOffer) { toast('No SDP offer for this call', 'error', 'The webhook payload may have arrived incomplete'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      streamRef.current = stream;
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.ontrack = (e) => { if (audioElRef.current) audioElRef.current.srcObject = e.streams[0]; };

      await pc.setRemoteDescription({ type: 'offer', sdp: call.sdpOffer });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      if (pc.iceGatheringState !== 'complete') {
        await new Promise((resolve) => {
          const check = () => { if (pc.iceGatheringState === 'complete') { pc.removeEventListener('icegatheringstatechange', check); resolve(); } };
          pc.addEventListener('icegatheringstatechange', check);
          setTimeout(resolve, 3000);
        });
      }

      const browserSdp = pc.localDescription?.sdp;
      if (!browserSdp) throw new Error('Failed to get browser SDP');

      await acceptWhatsAppCall(call.callId, browserSdp);

      setActiveCall(call);
      setCallStartedAt(Date.now());
      setMuted(false);
      setRingingCalls((prev) => prev.filter((c) => c.callId !== call.callId));
    } catch (e) {
      // The mic/peer connection may already be live at this point (a lost
      // race against another agent's Answer is now a real, expected way to
      // land here, not just a rare network blip) — release them, or the
      // browser's mic indicator stays on and the RTCPeerConnection leaks even
      // though this agent never actually got the call.
      streamRef.current?.getTracks().forEach((t) => t.stop());
      pcRef.current?.close();
      pcRef.current = null;
      streamRef.current = null;
      toast('Failed to answer call', 'error', e?.message || 'Microphone permission or network issue');
    }
  }, []);

  const declineCall = useCallback(async (call) => {
    try { await rejectWhatsAppCall(call.callId); } catch (e) { toast('Failed to decline', 'error', e?.message); }
    setRingingCalls((prev) => prev.filter((c) => c.callId !== call.callId));
    refreshHistory();
  }, [refreshHistory]);

  const hangUp = useCallback(async () => {
    if (!activeCall) return;
    try { await endWhatsAppCall(activeCall.callId); } catch (e) { toast('Failed to end call', 'error', e?.message); }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    pcRef.current?.close();
    pcRef.current = null;
    streamRef.current = null;
    setActiveCall(null);
    setCallStartedAt(null);
    refreshHistory();
  }, [activeCall, refreshHistory]);

  const toggleMute = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  }, [muted]);

  return {
    connection, ringingCalls, activeCall, callStartedAt, muted,
    history, historyStatus, refreshHistory,
    answerCall, declineCall, hangUp, toggleMute, attachAudioEl, reconnect,
  };
}
